import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Button, Spinner, Alert } from "react-bootstrap";
import { FaEdit, FaPrint, FaArrowLeft, FaFileInvoice } from "react-icons/fa";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import "./AddPurchase.css";

const ViewOrder = () => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { id } = useParams();
  const navigate = useNavigate();

  // Fetch order data from API
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await api.post("/order_detail.php", { id });
        
        if (response.data.head.code === 200) {
          setOrder(response.data.body);
        } else {
          setError(response.data.head.msg || "Failed to fetch order details");
        }
      } catch (err) {
        console.error("Error fetching order:", err);
        setError("Failed to load order details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchOrder();
    }
  }, [id]);

  const handlePrint = () => {
    if (order) {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Order #${order.id}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 40px; }
              .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #000; padding-bottom: 20px; }
              .company-info { margin-bottom: 20px; }
              .customer-info { margin-bottom: 30px; background: #f9f9f9; padding: 20px; border-radius: 5px; }
              .details { margin-bottom: 30px; }
              table { width: 100%; border-collapse: collapse; margin: 20px 0; }
              th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
              th { background-color: #2c3e50; color: white; }
              .total-section { margin-top: 30px; text-align: right; }
              .total-row { margin: 8px 0; font-size: 1.1em; }
              .grand-total { font-size: 1.3em; font-weight: bold; border-top: 2px solid #000; padding-top: 10px; }
              .status { padding: 5px 15px; border-radius: 4px; display: inline-block; font-weight: bold; }
              .status-pending { background-color: #ffc107; color: #000; }
              .status-confirmed { background-color: #17a2b8; color: #fff; }
              .status-processing { background-color: #007bff; color: #fff; }
              .status-completed { background-color: #28a745; color: #fff; }
              .status-cancelled { background-color: #dc3545; color: #fff; }
              .footer { margin-top: 50px; text-align: center; color: #666; font-size: 0.9em; }
              @media print {
                body { margin: 0; }
                .no-print { display: none; }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>ORDER INVOICE</h1>
              <h3>ORD-${order.id}</h3>
              <p>Order Date: ${order.order_date}</p>
              <p>Invoice Date: ${new Date().toLocaleDateString()}</p>
            </div>
            
            <div class="customer-info">
              <h3>Customer Details</h3>
              <p><strong>Name:</strong> ${order.customer_name || "N/A"}</p>
              <p><strong>Phone:</strong> ${order.customer_mobile || "N/A"}</p>
              <p><strong>Address:</strong> ${order.customer_address || "N/A"}</p>
              ${order.customer_city ? `<p><strong>City:</strong> ${order.customer_city}</p>` : ''}
              ${order.customer_state ? `<p><strong>State:</strong> ${order.customer_state}</p>` : ''}
              ${order.customer_pin_code ? `<p><strong>PIN Code:</strong> ${order.customer_pin_code}</p>` : ''}
              ${order.customer_gst_no ? `<p><strong>GST No:</strong> ${order.customer_gst_no}</p>` : ''}
              <p><strong>Status:</strong> <span class="status status-${order.status?.toLowerCase()}">${order.status}</span></p>
            </div>
            
            <h3>Order Items</h3>
            <table>
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Product Code</th>
                  <th>Item Name</th>
                  <th>Rate (₹)</th>
                  <th>Quantity</th>
                  <th>Total (₹)</th>
                </tr>
              </thead>
              <tbody>
                ${order.items?.map((item, index) => `
                  <tr>
                    <td>${index + 1}</td>
                    <td>${item.product_code}</td>
                    <td>${item.item_name}</td>
                    <td>₹${parseFloat(item.selling_rate).toFixed(2)}</td>
                    <td>${item.quantity}</td>
                    <td>₹${parseFloat(item.total).toFixed(2)}</td>
                  </tr>
                `).join('') || '<tr><td colspan="6">No items found</td></tr>'}
              </tbody>
            </table>
            
            <div class="total-section">
              <div class="total-row">
                <strong>Subtotal:</strong> ₹${parseFloat(order.sub_total || 0).toFixed(2)}
              </div>
              <div class="total-row">
                <strong>GST (${order.gst_percentage}%):</strong> ₹${parseFloat(order.gst_amount || 0).toFixed(2)}
              </div>
              <div class="total-row grand-total">
                <strong>Grand Total:</strong> ₹${parseFloat(order.grand_total || 0).toFixed(2)}
              </div>
            </div>
            
            <div class="footer">
              <p>Thank you for your business!</p>
              <p>This is a computer generated invoice.</p>
            </div>
            
            <script>
              window.onload = function() {
                window.print();
                setTimeout(function() {
                  window.close();
                }, 1000);
              }
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  const handleCreateInvoice = () => {
    // Navigate to create invoice page with order data
    navigate(`/create-invoice/${order.id}`, { state: { order } });
  };

  // const formatOrderId = (id) => {
  //   return `ORD-${id}`;
  // };

  const formatAddress = () => {
    if (!order) return "N/A";
    
    const parts = [
      order.customer_address,
      order.customer_city,
      order.customer_state,
      order.customer_pin_code
    ].filter(Boolean);
    
    return parts.join(", ");
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Order Details</h4>
          <Button className="btn-top" onClick={() => navigate(-1)}>
            <FaArrowLeft className="me-2" /> Back
          </Button>
        </div>
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading order details...</p>
        </div>
      </Container>
    );
  }

  if (error || !order) {
    return (
      <Container className="mt-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Order Details</h4>
          <Button className="btn-top" onClick={() => navigate(-1)}>
            <FaArrowLeft className="me-2" /> Back
          </Button>
        </div>
        <Alert variant="danger">
          {error || "Order not found."}
        </Alert>
        <Button className="mt-3" onClick={() => navigate("/order-list")}>
          Back to Order List
        </Button>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Order Details</h4>
        <div className="d-flex gap-2">
          {/* <Button className="btn-submit" onClick={handleCreateInvoice}>
            <FaFileInvoice className="me-2" /> Create Invoice
          </Button> */}
          {/* <Button className="btn-submit" onClick={handlePrint}>
            <FaPrint className="me-2" /> Print
          </Button> */}
          {/* <Button
            className="btn-submit"
            onClick={() => navigate(`/edit-order/${order.id}`)}
          >
            <FaEdit className="me-2" /> Edit
          </Button> */}
          <Button className="btn-top" onClick={() => navigate(-1)}>
            <FaArrowLeft className="me-2" /> Back
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      {/* Order ID, Date and Status Section */}
      <div className="p-4 border rounded bg-light mb-4">
        <Row>
          <Col lg={4} md={4} sm={12}>
            <div className="mb-3">
              <strong>Order ID:</strong>
              {/* <div className="fs-5 text-primary">{formatOrderId(order.id)}</div> */}
              <div className="fs-5 text-primary">{order.id}</div>
            </div>
          </Col>
          <Col lg={4} md={4} sm={12}>
            <div className="mb-3">
              <strong>Order Date:</strong>
              <div className="fs-5">{order.order_date}</div>
            </div>
          </Col>
          <Col lg={4} md={4} sm={12}>
            {/* <div className="mb-3">
              <strong>Status:</strong>
              <div className="fs-5">
                <span className={`badge bg-${getStatusColor(order.status)}`}>
                  {order.status}
                </span>
              </div>
            </div> */}
          </Col>
          {/* <Col lg={6} md={6} sm={12}>
            <div className="mb-3">
              <strong>Created At:</strong>
              <div className="fs-6">{order.created_at}</div>
            </div>
          </Col> */}
        </Row>
      </div>

      {/* Customer Details Section */}
      <div className="p-4 border rounded bg-light mb-4">
        <Row>
          <Col lg={3} md={3} sm={12}>
            <div className="mb-3">
              <strong>Customer Name:</strong>
              <div className="fs-5">{order.customer_name || "N/A"}</div>
            </div>
          </Col>
          <Col lg={3} md={3} sm={12}>
            <div className="mb-3">
              <strong>Phone Number:</strong>
              <div className="fs-5">{order.customer_mobile || "N/A"}</div>
            </div>
          </Col>
          <Col lg={6} md={6} sm={12}>
            <div className="mb-3">
              <strong>Complete Address:</strong>
              <div className="fs-6">{formatAddress()}</div>
            </div>
          </Col>
          {order.customer_gst_no && (
            <Col lg={3} md={3} sm={12}>
              <div className="mb-3">
                <strong>GST Number:</strong>
                <div className="fs-6">{order.customer_gst_no}</div>
              </div>
            </Col>
          )}
          {order.customer_city && (
            <Col lg={3} md={3} sm={12}>
              <div className="mb-3">
                <strong>City:</strong>
                <div className="fs-6">{order.customer_city}</div>
              </div>
            </Col>
          )}
          {order.customer_state && (
            <Col lg={3} md={3} sm={12}>
              <div className="mb-3">
                <strong>State:</strong>
                <div className="fs-6">{order.customer_state}</div>
              </div>
            </Col>
          )}
          {order.customer_pin_code && (
            <Col lg={3} md={3} sm={12}>
              <div className="mb-3">
                <strong>PIN Code:</strong>
                <div className="fs-6">{order.customer_pin_code}</div>
              </div>
            </Col>
          )}
        </Row>
      </div>

      {/* Order Items Table */}
      <div className="table-responsive">
        <Table bordered className="purchase-table">
          <thead className="table-dark">
            <tr>
              <th width="5%" className="text-center">S.No</th>
              <th width="15%">Product Code</th>
              <th width="25%">Item Name</th>
              <th width="15%">Selling Rate (₹)</th>
              <th width="10%">Quantity</th>
              <th width="15%" className="text-end">Total (₹)</th>
            </tr>
          </thead>
          <tbody>
            {order.items && order.items.length > 0 ? (
              order.items.map((item, index) => (
                <tr key={item.id || index}>
                  <td className="text-center">{index + 1}</td>
                  <td>{item.product_code}</td>
                  <td>{item.item_name}</td>
                  <td>₹{parseFloat(item.selling_rate || 0).toFixed(2)}</td>
                  <td>{item.quantity}</td>
                  <td className="text-end">₹{parseFloat(item.total || 0).toFixed(2)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center text-muted py-3">
                  No items found in this order
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Totals Section */}
      <Row className="mt-4">
        <Col xs={12} className="d-flex justify-content-end">
          <div className="totals-section">
            <div className="total-row">
              <span className="total-label">Subtotal:</span>
              <span className="total-value">₹{parseFloat(order.sub_total || 0).toFixed(2)}</span>
            </div>
            <div className="total-row">
              <span className="total-label">
                GST ({order.gst_percentage || 0}%)
              </span>
              <span className="total-value">₹{parseFloat(order.gst_amount || 0).toFixed(2)}</span>
            </div>
            <div className="total-row grand-total">
              <span className="total-label">Grand Total:</span>
              <span className="total-value">₹{parseFloat(order.grand_total || 0).toFixed(2)}</span>
            </div>
          </div>
        </Col>
      </Row>

      {/* Action Buttons */}
      {/* <div className="d-flex justify-content-end mt-4 gap-2">
        <Button className="btn-submit" onClick={handleCreateInvoice}>
          <FaFileInvoice className="me-2" /> Create Invoice
        </Button>
        <Button className="btn-top" onClick={handlePrint}>
          <FaPrint className="me-2" /> Print Order
        </Button>
        <Button
          className="btn-submit"
          onClick={() => navigate(`/edit-order/${order.id}`)}
        >
          <FaEdit className="me-2" /> Edit Order
        </Button>
        <Button className="btn-top" onClick={() => navigate("/order-list")}>
          <FaArrowLeft className="me-2" /> Back to Orders
        </Button>
      </div> */}
    </Container>
  );
};

// Helper function for status colors
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'pending': return 'warning';
    case 'confirmed': return 'info';
    case 'processing': return 'primary';
    case 'completed': return 'success';
    case 'cancelled': return 'danger';
    default: return 'secondary';
  }
};

export default ViewOrder;